/**
 * Shared layout for Enume legal pages (Terms, Acceptable Use, etc.)
 *
 * Extracted from TermsPage.jsx (v5, October 2026) so every legal page
 * shares one shell, one footer, and one link style instead of copies
 * that drift apart.
 *
 * Place at: src/components/legal/LegalLayout.jsx
 */

import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Sparkles, ArrowLeft } from 'lucide-react';
import { cn } from '../../utils/cn';

export const LINK =
  'text-purple-400 hover:text-purple-300 underline underline-offset-2';

const FOOTER_LINKS = [
  { to: '/terms',          label: 'Terms' },
  { to: '/acceptable-use', label: 'Acceptable Use' },
  { to: '/privacy',        label: 'Privacy' },
  { to: '/dmca',           label: 'DMCA' },
  { to: '/register',       label: 'Create Account' },
  { to: '/',               label: 'Home' },
];

export const PageShell = ({ current, children }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-300">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl" />
      </div>

      <header className="sticky top-0 z-30 bg-zinc-950/90 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            aria-label="Go back"
            className="p-2 -ml-2 rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-sm font-semibold text-white">Enume</span>
          </Link>
        </div>
      </header>

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-10 pb-24">
        {children}

        <nav className="mt-16 pt-8 border-t border-zinc-800/50 flex flex-wrap gap-x-4 gap-y-2 text-xs text-zinc-500">
          {FOOTER_LINKS.map(({ to, label }, i) => (
            <React.Fragment key={to}>
              {i > 0 && <span className="text-zinc-700" aria-hidden="true">·</span>}
              <Link
                to={to}
                aria-current={to === current ? 'page' : undefined}
                className={to === current ? 'text-zinc-300' : 'hover:text-zinc-300 transition-colors'}
              >
                {label}
              </Link>
            </React.Fragment>
          ))}
        </nav>
      </div>
    </div>
  );
};

export const Section = ({ id, title, children, accent = 'purple' }) => (
  <section id={id} className="scroll-mt-20">
    <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
      <span className={cn(
        'text-sm font-mono',
        accent === 'purple' ? 'text-purple-400/60' : 'text-amber-400/60'
      )}>{id}</span>
      {title}
    </h2>
    <div className="space-y-3 text-zinc-400 text-sm leading-relaxed">{children}</div>
  </section>
);

export const SubHead = ({ children }) => (
  <p className="text-white font-medium mt-3">{children}</p>
);

export const BulletList = ({ items }) => (
  <ul className="list-disc list-inside space-y-1 text-zinc-400 ml-2">
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);
